/**
 * Company Import Modal
 *
 * CSV upload wizard with drag-drop, validation preview, and import execution.
 * Steps: Upload → Validate → Review → Import
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Download,
} from 'lucide-react';
import { useValidateCompanyImport, useImportCompanies } from '../../hooks';
import { useTestMode } from '../../contexts/TestModeContext';

const TEMPLATE_CSV = `legal_name,trading_name,registration_number,incorporation_country,incorporation_date,legal_form,industry,website,email,phone
"Acme Corp","Acme","12345678","US","2020-01-15","LLC","Technology","https://acme.com","info@acme.com","+1-555-0100"
"GlobalTech Ltd","","87654321","GB","2019-06-01","Ltd","Finance","","contact@globaltech.co.uk",""`;

export default function CompanyImportModal({ onClose, onImported }) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const { isTestMode } = useTestMode();
  const validateMutation = useValidateCompanyImport();
  const importMutation = useImportCompanies();

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size must be under 5 MB');
      return;
    }
    setFile(selectedFile);
    setError('');
    setValidationResult(null);
    setImportResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleValidate = async () => {
    if (!file) return;
    setStep('validating');
    setError('');
    try {
      const result = await validateMutation.mutateAsync({
        file,
        isSandbox: isTestMode,
      });
      setValidationResult(result);
      setStep('review');
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Validation failed');
      setStep('upload');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    setError('');
    try {
      const result = await importMutation.mutateAsync({
        file,
        isSandbox: isTestMode,
      });
      setImportResult(result);
      setStep('done');
      if (result.created > 0 && onImported) {
        onImported(result);
      }
    } catch (err) {
      const detail = err?.data?.detail;
      if (typeof detail === 'object' && detail?.errors) {
        setValidationResult({
          total_rows: detail.total_rows,
          valid_rows: 0,
          invalid_rows: detail.failed,
          errors: detail.errors,
        });
        setStep('review');
        setError(detail.message || 'Import failed due to validation errors');
      } else {
        setError(detail || err?.message || 'Import failed');
        setStep('review');
      }
    }
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'company_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const reset = () => {
    setFile(null);
    setStep('upload');
    setValidationResult(null);
    setImportResult(null);
    setError('');
  };

  return (
    <div className="import-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        .import-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
          animation: importFadeIn 0.15s ease-out;
        }
        @keyframes importFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .import-modal {
          background: var(--bg-primary, #0a0b0e);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          width: 100%;
          max-width: 560px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
        }
        .import-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border-primary, #23262f);
        }
        .import-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }
        .import-close {
          padding: 8px;
          background: transparent;
          border: none;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
          border-radius: 6px;
        }
        .import-close:hover {
          color: var(--text-primary, #f0f2f5);
          background: var(--bg-secondary, #111318);
        }
        .import-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }
        .drop-zone {
          border: 2px dashed var(--border-primary, #23262f);
          border-radius: 12px;
          padding: 40px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.15s;
        }
        .drop-zone:hover, .drop-zone.active {
          border-color: var(--accent-primary);
          background: rgba(99, 102, 241, 0.05);
        }
        .drop-zone-icon {
          width: 48px;
          height: 48px;
          color: var(--text-secondary, #8b919e);
          margin: 0 auto 12px;
        }
        .drop-zone-text {
          font-size: 14px;
          color: var(--text-primary, #f0f2f5);
          margin-bottom: 4px;
        }
        .drop-zone-hint {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
        }
        .file-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-secondary, #111318);
          border-radius: 8px;
          margin-top: 16px;
        }
        .file-info svg {
          width: 20px;
          height: 20px;
          color: var(--accent-primary);
          flex-shrink: 0;
        }
        .file-name {
          font-size: 14px;
          color: var(--text-primary, #f0f2f5);
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .file-size {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          flex-shrink: 0;
        }
        .file-remove {
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
          border-radius: 4px;
        }
        .file-remove:hover {
          color: #ef4444;
        }
        .template-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          font-size: 13px;
          color: var(--accent-primary);
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          padding: 0;
        }
        .template-link:hover {
          text-decoration: underline;
        }
        .validation-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .validation-stat {
          padding: 12px;
          background: var(--bg-secondary, #111318);
          border-radius: 8px;
          text-align: center;
        }
        .validation-stat-value {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
        }
        .validation-stat-label {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          margin-top: 4px;
        }
        .validation-stat.success .validation-stat-value { color: #22c55e; }
        .validation-stat.error .validation-stat-value { color: #ef4444; }
        .error-list {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
        }
        .error-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--border-primary, #23262f);
          font-size: 13px;
        }
        .error-row:last-child { border-bottom: none; }
        .error-row svg {
          width: 14px;
          height: 14px;
          color: #ef4444;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .error-row-num {
          color: var(--text-secondary, #8b919e);
          flex-shrink: 0;
          min-width: 50px;
        }
        .error-row-msg {
          color: var(--text-primary, #f0f2f5);
          flex: 1;
        }
        .error-row-field {
          color: var(--accent-primary);
          font-family: monospace;
          font-size: 12px;
        }
        .import-error {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 8px;
          font-size: 13px;
          color: #ef4444;
          margin-bottom: 16px;
        }
        .import-error svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
        .import-success {
          text-align: center;
          padding: 24px;
        }
        .import-success svg {
          width: 48px;
          height: 48px;
          color: #22c55e;
          margin-bottom: 16px;
        }
        .import-success h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 8px;
        }
        .import-success p {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
          margin: 0;
        }
        .import-loading {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary, #8b919e);
        }
        .import-loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--border-primary, #23262f);
          border-top-color: var(--accent-primary);
          border-radius: 50%;
          animation: importSpin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        @keyframes importSpin {
          to { transform: rotate(360deg); }
        }
        .import-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-primary, #23262f);
        }
        .import-btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
          border: none;
        }
        .import-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .import-btn-secondary {
          background: var(--bg-secondary, #111318);
          color: var(--text-primary, #f0f2f5);
          border: 1px solid var(--border-primary, #23262f);
        }
        .import-btn-secondary:hover:not(:disabled) {
          background: var(--bg-tertiary, #1a1d24);
        }
        .import-btn-primary {
          background: var(--accent-primary);
          color: white;
        }
        .import-btn-primary:hover:not(:disabled) {
          background: var(--accent-hover, #5558e3);
        }
      `}</style>

      <div className="import-modal">
        <div className="import-header">
          <h3 className="import-title">
            <Upload size={20} />
            Import Companies
          </h3>
          <button className="import-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="import-body">
          {error && (
            <div className="import-error">
              <AlertTriangle />
              <span>{error}</span>
            </div>
          )}

          {/* Upload Step */}
          {step === 'upload' && (
            <>
              <div
                className={`drop-zone ${dragOver ? 'active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="drop-zone-icon" />
                <div className="drop-zone-text">
                  Drop a CSV file here, or click to browse
                </div>
                <div className="drop-zone-hint">
                  Max 500 rows &middot; 5 MB limit &middot; CSV format only
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />

              {file && (
                <div className="file-info">
                  <FileText />
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                  <button className="file-remove" onClick={(e) => { e.stopPropagation(); reset(); }}>
                    <X size={16} />
                  </button>
                </div>
              )}

              <button className="template-link" onClick={handleDownloadTemplate}>
                <Download size={14} />
                Download CSV template
              </button>
            </>
          )}

          {/* Validating Step */}
          {step === 'validating' && (
            <div className="import-loading">
              <div className="import-loading-spinner" />
              <div>Validating {file?.name}...</div>
            </div>
          )}

          {/* Review Step */}
          {step === 'review' && validationResult && (
            <>
              <div className="validation-summary">
                <div className="validation-stat">
                  <div className="validation-stat-value">{validationResult.total_rows}</div>
                  <div className="validation-stat-label">Total Rows</div>
                </div>
                <div className={`validation-stat ${validationResult.valid_rows > 0 ? 'success' : ''}`}>
                  <div className="validation-stat-value">{validationResult.valid_rows}</div>
                  <div className="validation-stat-label">Valid</div>
                </div>
                <div className={`validation-stat ${validationResult.invalid_rows > 0 ? 'error' : ''}`}>
                  <div className="validation-stat-value">{validationResult.invalid_rows}</div>
                  <div className="validation-stat-label">Invalid</div>
                </div>
              </div>

              {validationResult.errors?.length > 0 && (
                <div className="error-list">
                  {validationResult.errors.map((err, i) => (
                    <div key={i} className="error-row">
                      <XCircle />
                      <span className="error-row-num">Row {err.row}</span>
                      {err.field && <span className="error-row-field">{err.field}</span>}
                      <span className="error-row-msg">{err.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {validationResult.valid_rows > 0 && validationResult.invalid_rows === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)', borderRadius: 8, fontSize: 13, color: '#22c55e', marginTop: 16 }}>
                  <CheckCircle size={16} />
                  All rows validated successfully. Ready to import.
                </div>
              )}
            </>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="import-loading">
              <div className="import-loading-spinner" />
              <div>Importing {validationResult?.valid_rows || 0} companies...</div>
            </div>
          )}

          {/* Done Step */}
          {step === 'done' && importResult && (
            <div className="import-success">
              <CheckCircle />
              <h3>Import Complete</h3>
              <p>
                {importResult.created} {importResult.created === 1 ? 'company' : 'companies'} created successfully
                {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
              </p>
            </div>
          )}
        </div>

        <div className="import-footer">
          {step === 'upload' && (
            <>
              <button className="import-btn import-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="import-btn import-btn-primary"
                disabled={!file}
                onClick={handleValidate}
              >
                Validate
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button className="import-btn import-btn-secondary" onClick={reset}>
                Start Over
              </button>
              <button
                className="import-btn import-btn-primary"
                disabled={!validationResult || validationResult.invalid_rows > 0}
                onClick={handleImport}
              >
                Import {validationResult?.valid_rows || 0} Companies
              </button>
            </>
          )}
          {step === 'done' && (
            <button className="import-btn import-btn-primary" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
